/**
 * @file This file defines the FilterContext, which provides a centralized state management
 * system for all filter-related functionality in the application. It handles filter state,
 * URL synchronization, ZIP code validation and geocoding, and transforming filters to API parameters.
 */
import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useStateValue } from '../../../../store/store.jsx';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFiltersFromURL, validateURLParams } from '../../../../utils/url';
import { PAGE_FILTER_CONFIGS } from '../../config/pageFilterConfigs';
import { URL_PARAM_MAPPING } from '../../constants/urlParams';
import { getLocationFilters } from '../../utils/locationUtils';
import { isValidZipFormat } from '../../utils/locationUtils';
import { AUTO_APPLY_ENABLED, AUTO_APPLY_DELAY_MS, AUTO_APPLY_DELAYS_BY_TYPE } from '../../constants/autoApply';

/**
 * Action types for the filter reducer.
 * These define all possible actions that can be dispatched to modify filter state.
 */
export const FilterActionTypes = {
	SET_FILTER: 'SET_FILTER', // Set a specific filter value
	APPLY_FILTERS: 'APPLY_FILTERS', // Apply current filter values (marks them as "applied")
	CLEAR_FILTERS: 'CLEAR_FILTERS', // Reset all filters to initial state
	REMOVE_FILTER: 'REMOVE_FILTER', // Remove a specific filter value
	SET_BASE_FILTERS: 'SET_BASE_FILTERS', // Set base filters (e.g., disease, intervention)
	SET_ZIP_COORDINATES: 'SET_ZIP_COORDINATES', // Set coordinates for ZIP code
	SCHEDULE_AUTO_APPLY: 'SCHEDULE_AUTO_APPLY', // Schedule auto-apply timer
	CANCEL_AUTO_APPLY: 'CANCEL_AUTO_APPLY', // Cancel pending auto-apply
	START_AUTO_APPLY: 'START_AUTO_APPLY', // Start auto-apply process
	RESET_INITIAL_LOAD: 'RESET_INITIAL_LOAD', // Reset isInitialLoad flag after URL sync
	SET_INVALID_QUERY: 'SET_INVALID_QUERY', // Set invalid query state when URL params are invalid
	SET_VALIDATION_PENDING: 'SET_VALIDATION_PENDING', // Mark a filter as pending async validation
	SET_VALIDATION_COMPLETE: 'SET_VALIDATION_COMPLETE', // Mark a filter validation as complete
};

/**
 * Initial state for the filter reducer.
 * Defines the default structure and values for the filter state.
 */
const initialState = {
	filters: {
		maintype: [],
		subtype: [],
		stage: [],
		drugIntervention: [],
		age: '',
		location: {
			zipCode: '',
			radius: null,
		},
	},
	appliedFilters: {
		maintype: [],
		subtype: [],
		stage: [],
		drugIntervention: [],
		age: '',
		location: {
			zipCode: '',
			radius: null,
		},
	},
	isInitializingFromURL: false, // Filters that have been applied (after clicking "Apply")
	baseFilters: {}, // Base filters that are always applied (e.g., disease type)
	isDirty: false, // Whether filters have been modified but not applied
	shouldSearch: true, // Whether a search should be triggered
	isInitialLoad: true, // Whether this is the initial page load
	currentPage: null, // Current page number
	paramOrder: [], // Order of URL parameters
	preservedParams: '', // URL parameters to preserve
	zipCoords: null, // Coordinates for the current ZIP code
	appliedZipCoords: null, // Coordinates for the applied ZIP code
	autoApplyTimerId: null, // Timer ID for auto-apply
	isAutoApplying: false, // Whether auto-apply is in progress
	pendingAutoApply: false, // Whether auto-apply is pending
	lastChangedFilter: null, // Track which filter was last changed
	isInvalidQuery: false, // Whether URL contains invalid query parameters
	invalidParams: null, // The invalid URL parameters that were detected (for analytics)
	pendingValidations: {
		maintype: false,
		subtype: false,
		stage: false,
		drugIntervention: false,
	}, // Track which filters are pending async validation
	validationComplete: true, // Whether all async validations are complete
};

/**
 * Reducer function for managing filter state.
 * Handles all actions defined in FilterActionTypes.
 *
 * @param {object} state - Current filter state
 * @param {object} action - Action object with type and payload
 * @returns {object} New filter state
 */
function filterReducer(state, action) {
	const { enabledFilters } = PAGE_FILTER_CONFIGS[state.pageType];
	const clearedFilters = {};
	let isNewPageLoad = null;
	let preservedParams = null;
	let pageNumber = null;
	let paramPairs = null;
	let orderedParams = null;
	switch (action.type) {
		case FilterActionTypes.SET_FILTER: {
			// // Log the incoming action
			// console.log('[FilterContext] SET_FILTER action - filterType:', action.payload.filterType, 'value:', action.payload.value);
			// console.log('[FilterContext] SET_FILTER - current state.filters:', state.filters);
			// console.log('[FilterContext] SET_FILTER - current state.appliedFilters:', state.appliedFilters);
			// Ignore if filter type is not enabled for this page
			if (!enabledFilters.includes(action.payload.filterType)) {
				// console.log('[FilterContext] SET_FILTER - filterType not enabled:', action.payload.filterType);
				return state;
			}

			const newFilters = { ...state.filters };
			// console.log('FilterContext - SET_FILTER - newFilters before modification:', newFilters);
			if (action.payload.filterType === 'maintype') {
				// console.log('[MAINTYPE DEBUG] Setting maintype, isNewPageLoad:', action.payload.isNewPageLoad, 'isInitializingFromURL:', state.isInitializingFromURL);
				// console.log('[MAINTYPE DEBUG] Current subtype before:', newFilters.subtype);
				newFilters[action.payload.filterType] = action.payload.value;
				// Only clear subtypes and stages when maintype changes from user interaction, not during URL initialization
				// Check both isNewPageLoad flag and isInitializingFromURL state
				if (!action.payload.isNewPageLoad && !state.isInitializingFromURL) {
					// console.log('[MAINTYPE DEBUG] Clearing subtype because NOT new page load AND NOT initializing from URL');
					newFilters.subtype = []; // Clear subtypes when maintype changes
					newFilters.stage = []; // Clear stages when maintype changes
				} else {
					// console.log('[MAINTYPE DEBUG] Preserving subtype because IS new page load OR IS initializing from URL');
				}

				// Special case: Apply immediately for maintype changes (both select and clear)
				// BUT not during URL initialization where multiple filters need to be set first
				if (AUTO_APPLY_ENABLED && !action.payload.isNewPageLoad) {
					return {
						...state,
						filters: newFilters,
						appliedFilters: { ...newFilters },
						isDirty: false,
						shouldSearch: true,
						pendingAutoApply: false,
						lastChangedFilter: action.payload.filterType,
					};
				}
			} else if (action.payload.filterType === 'subtype' || action.payload.filterType === 'stage' || action.payload.filterType === 'drugIntervention') {
				if (action.payload.filterType === 'subtype') {
					// console.log('[SUBTYPE DEBUG] Setting subtype:', action.payload.value, 'isNewPageLoad:', action.payload.isNewPageLoad);
				}
				newFilters[action.payload.filterType] = action.payload.value;
				// console.log('[FilterContext] SET_FILTER - drugIntervention branch, newFilters after update:', newFilters);

				// Apply immediately for subtype, stage, and drugIntervention changes (both select and clear)
				// BUT not during URL initialization where multiple filters need to be set first
				if (AUTO_APPLY_ENABLED && !action.payload.isNewPageLoad) {
					const newState = {
						...state,
						filters: newFilters,
						appliedFilters: { ...newFilters },
						isDirty: false,
						shouldSearch: true,
						pendingAutoApply: false,
						lastChangedFilter: action.payload.filterType,
					};
					// console.log('[FilterContext] SET_FILTER - returning new state with filters:', newState.filters);
					// console.log('[FilterContext] SET_FILTER - returning new state with appliedFilters:', newState.appliedFilters);
					return newState;
				}
			} else {
				newFilters[action.payload.filterType] = action.payload.value;
			}

			let newIsDirty = false;
			// Compare newFilters with appliedFilters if appliedFilters is not empty
			if (Object.keys(state.appliedFilters).length > 0) {
				for (const key in newFilters) {
					// Ensure the key exists in appliedFilters before comparison,
					// or if it doesn't, it means a new filter type was added (hence dirty).
					// Also check if the key is part of the initial state's filter structure.
					if (Object.prototype.hasOwnProperty.call(initialState.filters, key)) {
						const filterValue = newFilters[key];
						const appliedValue = state.appliedFilters[key];

						if (typeof filterValue === 'object' && filterValue !== null) {
							if (JSON.stringify(filterValue) !== JSON.stringify(appliedValue)) {
								newIsDirty = true;
								break;
							}
						} else {
							if (filterValue !== appliedValue) {
								newIsDirty = true;
								break;
							}
						}
					} else {
						// A key exists in newFilters that wasn't in initialState.filters, consider it dirty.
						if (newFilters[key] !== undefined && newFilters[key] !== null && (Array.isArray(newFilters[key]) ? newFilters[key].length > 0 : newFilters[key] !== '')) {
							newIsDirty = true;
							break;
						}
					}
				}
			} else {
				// If appliedFilters is empty (e.g., initial load, or after clear),
				// compare with initialState.filters.
				// It's dirty if any filter in newFilters differs from its corresponding initial default.
				for (const key in newFilters) {
					if (Object.prototype.hasOwnProperty.call(initialState.filters, key)) {
						const filterValue = newFilters[key];
						const initialDefaultValue = initialState.filters[key];
						if (typeof filterValue === 'object' && filterValue !== null) {
							if (JSON.stringify(filterValue) !== JSON.stringify(initialDefaultValue)) {
								newIsDirty = true;
								break;
							}
						} else {
							if (filterValue !== initialDefaultValue) {
								newIsDirty = true;
								break;
							}
						}
					} else {
						// Similar to above, if a new key appears that wasn't in initial defaults and has a value.
						if (newFilters[key] !== undefined && newFilters[key] !== null && (Array.isArray(newFilters[key]) ? newFilters[key].length > 0 : newFilters[key] !== '')) {
							newIsDirty = true;
							break;
						}
					}
				}
			}

			// Clear zipCoords when location zipCode changes to prevent stale coordinates
			let newZipCoords = state.zipCoords;
			if (action.payload.filterType === 'location') {
				const oldZipCode = state.filters.location?.zipCode;
				const newZipCode = action.payload.value?.zipCode;
				// If zipcode changed, clear old coordinates
				if (oldZipCode !== newZipCode) {
					newZipCoords = null;
				}
			}

			return {
				...state,
				filters: newFilters,
				zipCoords: newZipCoords,
				isDirty: newIsDirty,
				pendingAutoApply: AUTO_APPLY_ENABLED && newIsDirty,
				lastChangedFilter: action.payload.filterType,
			};
		}
		case FilterActionTypes.APPLY_FILTERS: {
			// console.log('FilterContext - APPLY_FILTERS - current state filters:', state.filters);
			// console.log('FilterContext - APPLY_FILTERS - current state appliedFilters:', state.appliedFilters);

			isNewPageLoad = action.payload?.isNewPageLoad;
			preservedParams = action.payload?.preservedParams;

			// Process preserved URL parameters
			paramPairs = (preservedParams || '').split('&').filter(Boolean);
			orderedParams = new Map();
			paramPairs.forEach((pair) => {
				const [key, value] = pair.split('=');
				if (key) orderedParams.set(key, value);
			});

			pageNumber = isNewPageLoad ? orderedParams.get('pn') || '1' : '1';

			// Handle ZIP coordinates
			let newAppliedZipCoords = null;

			// Only apply coordinates if we have both a ZIP code and coordinates
			if (state.filters.location?.zipCode && state.zipCoords) {
				newAppliedZipCoords = state.zipCoords;
			}

			// Ensure we're not accidentally clearing user selections
			const filtersToApply = state.filters;
			// console.log('FilterContext - APPLY_FILTERS - filters to apply:', filtersToApply);

			// Create appliedFilters with validation - exclude invalid filters
			const appliedFiltersToSet = { ...filtersToApply };

			// Validate age filter before adding to appliedFilters
			if (appliedFiltersToSet.age && Array.isArray(appliedFiltersToSet.age)) {
				const validAgeValues = appliedFiltersToSet.age.filter((age) => {
					// Check if the string contains only digits
					if (!/^\d+$/.test(age)) {
						return false; // Invalid: contains non-numeric characters
					}
					const numAge = parseInt(age, 10);
					return !isNaN(numAge) && numAge >= 0 && numAge <= 120;
				});
				if (validAgeValues.length === 0) {
					// All age values are invalid, remove the age filter from appliedFilters
					delete appliedFiltersToSet.age;
				} else {
					// Keep only valid age values
					appliedFiltersToSet.age = validAgeValues;
				}
			}

			// Validate location filter before adding to appliedFilters
			if (appliedFiltersToSet.location?.zipCode && (!isValidZipFormat(appliedFiltersToSet.location.zipCode) || !state.zipCoords)) {
				// Remove invalid location filter from appliedFilters (but keep it in filters for UI)
				delete appliedFiltersToSet.location;
			}

			const newState = {
				...state,
				filters: filtersToApply, // Preserve current filter selections (including invalid ones for UI)
				appliedFilters: appliedFiltersToSet, // Only valid filters are applied
				isDirty: false, // No longer dirty after applying
				shouldSearch: true, // Should trigger a search
				isInitialLoad: isNewPageLoad || false, // Only true for new page loads
				preservedParams: preservedParams || '',
				currentPage: pageNumber || '1',
				paramOrder: Array.from(orderedParams.keys()),
				appliedZipCoords: newAppliedZipCoords,
				autoApplyTimerId: null,
				isAutoApplying: false,
				pendingAutoApply: false,
			};

			// console.log('FilterContext - APPLY_FILTERS - isNewPageLoad:', isNewPageLoad, 'resulting isInitialLoad:', newState.isInitialLoad);

			// console.log('FilterContext - APPLY_FILTERS - new state filters:', newState.filters);
			// console.log('FilterContext - APPLY_FILTERS - new state appliedFilters:', newState.appliedFilters);

			return newState;
		}

		case FilterActionTypes.CLEAR_FILTERS:
			// Reset all enabled filters to their initial state
			enabledFilters.forEach((filterType) => {
				clearedFilters[filterType] = initialState.filters[filterType];
			});

			return {
				...state,
				filters: clearedFilters,
				appliedFilters: { ...clearedFilters },
				isDirty: false,
				shouldSearch: true,
				appliedZipCoords: null,
				pendingAutoApply: false,
				autoApplyTimerId: null,
				// Don't clear isInvalidQuery here - it should only be cleared when user manually clears or valid params load
			};

		case FilterActionTypes.REMOVE_FILTER: {
			const { filterType, value } = action.payload;
			const updatedFilters = { ...state.filters };

			// Handle array filters (remove specific value)
			if (Array.isArray(updatedFilters[filterType])) {
				updatedFilters[filterType] = updatedFilters[filterType].filter((v) => v !== value);
			}
			// Handle location filter (reset both ZIP and radius)
			else if (filterType === 'location') {
				updatedFilters.location = {
					zipCode: '',
					radius: null,
				};
			} else {
				updatedFilters[filterType] = null;
			}

			// Update appliedFilters as well when auto-apply is enabled
			let updatedAppliedFilters = state.appliedFilters;
			if (AUTO_APPLY_ENABLED) {
				updatedAppliedFilters = { ...updatedFilters };
			}

			return {
				...state,
				filters: updatedFilters,
				appliedFilters: updatedAppliedFilters,
				isDirty: !AUTO_APPLY_ENABLED,
				pendingAutoApply: AUTO_APPLY_ENABLED,
				lastChangedFilter: filterType,
			};
		}

		case FilterActionTypes.SET_BASE_FILTERS:
			return {
				...state,
				baseFilters: action.payload,
			};
		case 'SET_URL_INIT_FLAG':
			return {
				...state,
				isInitializingFromURL: action.payload,
			};

		case FilterActionTypes.SET_ZIP_COORDINATES:
			return {
				...state,
				zipCoords: action.payload,
			};

		case FilterActionTypes.SCHEDULE_AUTO_APPLY:
			return {
				...state,
				autoApplyTimerId: action.payload.timerId,
			};

		case FilterActionTypes.CANCEL_AUTO_APPLY:
			// Clear the timer if it exists
			if (state.autoApplyTimerId) {
				clearTimeout(state.autoApplyTimerId);
			}
			return {
				...state,
				autoApplyTimerId: null,
				pendingAutoApply: false,
			};

		case FilterActionTypes.START_AUTO_APPLY:
			return {
				...state,
				isAutoApplying: true,
				pendingAutoApply: false,
			};

		case FilterActionTypes.RESET_INITIAL_LOAD:
			return {
				...state,
				isInitialLoad: false,
			};

		case FilterActionTypes.SET_INVALID_QUERY:
			return {
				...state,
				isInvalidQuery: action.payload.isInvalid !== undefined ? action.payload.isInvalid : action.payload,
				invalidParams:
					action.payload.invalidParams === null
						? null // Explicit clear
						: action.payload.invalidParams !== undefined
						? { ...state.invalidParams, ...action.payload.invalidParams } // Merge with existing
						: action.payload
						? state.invalidParams
						: null,
			};

		case FilterActionTypes.SET_VALIDATION_PENDING: {
			const filterType = action.payload;
			const newPendingValidations = {
				...state.pendingValidations,
				[filterType]: true,
			};
			return {
				...state,
				pendingValidations: newPendingValidations,
				validationComplete: false,
			};
		}

		case FilterActionTypes.SET_VALIDATION_COMPLETE: {
			const filterType = action.payload;
			const newPendingValidations = {
				...state.pendingValidations,
				[filterType]: false,
			};
			// Check if all validations are now complete
			const allComplete = !Object.values(newPendingValidations).some((pending) => pending);
			return {
				...state,
				pendingValidations: newPendingValidations,
				validationComplete: allComplete,
			};
		}

		default:
			return state;
	}
}

/**
 * Context for providing filter state and actions throughout the application.
 */
export const FilterContext = createContext();

/**
 * Provider component for the FilterContext.
 * Manages filter state, URL synchronization, and filter application.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {object} [props.baseFilters={}] - Base filters to always apply
 * @param {string} [props.pageType='Disease'] - Type of page (e.g., 'Disease', 'Intervention')
 * @returns {JSX.Element} FilterContext.Provider component
 */
export function FilterProvider({ children, baseFilters = {}, pageType = 'Disease' }) {
	const filterConfig = PAGE_FILTER_CONFIGS[pageType];
	const [state, dispatch] = useReducer(filterReducer, {
		...initialState,
		baseFilters,
		pageType,
	});

	const [isApplyingFilters, setIsApplyingFilters] = useState(false);
	const [{ zipConversionEndpoint }] = useStateValue();
	// Track when we've just removed invalid params to prevent clearing the error message on re-render
	const justRemovedInvalidParamsRef = React.useRef(false);

	const location = useLocation();
	const navigate = useNavigate();

	// Extract only filter-related params from URL for dependency tracking
	// This prevents the effect from running when non-filter params (like pn for pagination) change
	const filterParamsString = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const filterParamKeys = [URL_PARAM_MAPPING.maintype.shortCode, URL_PARAM_MAPPING.subtype.shortCode, URL_PARAM_MAPPING.stage.shortCode, URL_PARAM_MAPPING.drugIntervention.shortCode, URL_PARAM_MAPPING.age.shortCode, URL_PARAM_MAPPING.zipCode.shortCode, URL_PARAM_MAPPING.radius.shortCode];
		return filterParamKeys
			.filter((key) => params.has(key))
			.map((key) => `${key}=${params.get(key)}`)
			.sort()
			.join('&');
	}, [location.search]);

	/**
	 * Effect to initialize filters from URL parameters when the path changes.
	 * Extracts filter values from URL and dispatches actions to set them.
	 */
	useEffect(() => {
		let params = new URLSearchParams(location.search);
		let hasInvalidParams = false;
		const detectedInvalidParams = {}; // Track which params are invalid for analytics

		// Check for invalid zipcode
		const zipFromUrl = params.get(URL_PARAM_MAPPING.zipCode.shortCode);
		const radiusFromUrl = params.get(URL_PARAM_MAPPING.radius.shortCode);
		const hasInvalidZip = zipFromUrl && !/^\d{5}$/.test(zipFromUrl);
		if (hasInvalidZip) {
			hasInvalidParams = true;
			detectedInvalidParams[URL_PARAM_MAPPING.zipCode.shortCode] = zipFromUrl;
		}

		// Validate URL parameters for age, subtype/stage dependencies
		const maintypeValue = params.get(URL_PARAM_MAPPING.maintype.shortCode);
		const validationResult = validateURLParams(params, !!maintypeValue);

		if (!validationResult.isValid) {
			hasInvalidParams = true;
			// Collect the invalid params from validation result
			if (validationResult.invalidParams) {
				Object.assign(detectedInvalidParams, validationResult.invalidParams);
			}
		}

		// If ANY parameter is invalid, strip ALL filter params from URL and set error state
		if (hasInvalidParams) {
			// Remove ALL filter parameters from URL
			params.delete(URL_PARAM_MAPPING.maintype.shortCode);
			params.delete(URL_PARAM_MAPPING.subtype.shortCode);
			params.delete(URL_PARAM_MAPPING.stage.shortCode);
			params.delete(URL_PARAM_MAPPING.age.shortCode);
			params.delete(URL_PARAM_MAPPING.drugIntervention.shortCode);
			params.delete(URL_PARAM_MAPPING.zipCode.shortCode);
			params.delete(URL_PARAM_MAPPING.radius.shortCode);

			// Mark that we just removed invalid params (ref is checked on re-render)
			justRemovedInvalidParamsRef.current = true;

			// Update URL without the filter parameters using navigate for proper React Router integration
			const newSearch = params.toString();
			const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
			navigate(newUrl, { replace: true, state: { invalidParamsRemoved: true } });

			// Clear all filters when invalid parameters are detected
			dispatch({ type: FilterActionTypes.CLEAR_FILTERS });

			// If zipcode was invalid, populate the form with the invalid value
			// so ZipCodeFilter can show its own validation error
			if (hasInvalidZip) {
				dispatch({
					type: FilterActionTypes.SET_FILTER,
					payload: {
						filterType: 'location',
						value: {
							zipCode: zipFromUrl,
							radius: radiusFromUrl || '100',
						},
						isNewPageLoad: true,
					},
				});
			}

			dispatch({
				type: FilterActionTypes.APPLY_FILTERS,
				payload: { isNewPageLoad: true, preservedParams: '' },
			});

			// Mark that we have invalid query parameters (after clearing filters)
			// Include the invalid params for analytics
			dispatch({
				type: FilterActionTypes.SET_INVALID_QUERY,
				payload: {
					isInvalid: true,
					invalidParams: detectedInvalidParams,
				},
			});

			// Exit early - don't try to load filters from invalid params
			return;
		} else if (!justRemovedInvalidParamsRef.current && !location.state?.invalidParamsRemoved) {
			// Only clear invalid query flag if we didn't just remove invalid params
			// This prevents the second render from clearing the error message
			dispatch({ type: FilterActionTypes.SET_INVALID_QUERY, payload: { isInvalid: false, invalidParams: null } });
		}

		// Reset the ref after checking (for future navigations)
		justRemovedInvalidParamsRef.current = false;

		// Mark async validations as pending for filter params that need API validation
		// These will be marked complete by the respective filter components after they validate
		if (params.get(URL_PARAM_MAPPING.maintype.shortCode)) {
			dispatch({ type: FilterActionTypes.SET_VALIDATION_PENDING, payload: 'maintype' });
		}
		if (params.get(URL_PARAM_MAPPING.subtype.shortCode)) {
			dispatch({ type: FilterActionTypes.SET_VALIDATION_PENDING, payload: 'subtype' });
		}
		if (params.get(URL_PARAM_MAPPING.stage.shortCode)) {
			dispatch({ type: FilterActionTypes.SET_VALIDATION_PENDING, payload: 'stage' });
		}
		if (params.get(URL_PARAM_MAPPING.drugIntervention.shortCode)) {
			dispatch({ type: FilterActionTypes.SET_VALIDATION_PENDING, payload: 'drugIntervention' });
		}

		let filtersFromUrl = getFiltersFromURL(params);
		let isNewPageLoad = true;

		// Preserve non-filter URL parameters
		let paramOrder = Array.from(params.keys());
		let filterParams = [URL_PARAM_MAPPING.maintype.shortCode, URL_PARAM_MAPPING.subtype.shortCode, URL_PARAM_MAPPING.stage.shortCode, URL_PARAM_MAPPING.drugIntervention.shortCode, URL_PARAM_MAPPING.age.shortCode, URL_PARAM_MAPPING.zipCode.shortCode, URL_PARAM_MAPPING.radius.shortCode];
		let preservedParamsMap = new Map();

		for (const key of paramOrder) {
			if (!filterParams.includes(key)) {
				preservedParamsMap.set(key, params.get(key));
			}
		}

		const preservedParams = Array.from(preservedParamsMap.entries())
			.map(([key, value]) => `${key}=${value}`)
			.join('&');

		// Set filters from URL if they exist
		// console.log('[FilterContext URL Effect] filtersFromUrl:', filtersFromUrl);
		// console.log('[FilterContext URL Effect] current state.filters:', state.filters);
		// console.log('[FilterContext URL Effect] current state.appliedFilters:', state.appliedFilters);

		if (Object.keys(filtersFromUrl).length > 0) {
			// Set flag that we're initializing from URL
			dispatch({ type: 'SET_URL_INIT_FLAG', payload: true });

			Object.entries(filtersFromUrl).forEach(([filterType, value]) => {
				dispatch({
					type: FilterActionTypes.SET_FILTER,
					payload: { filterType, value, isNewPageLoad },
				});
			});

			// Clear the flag after all filters are set
			dispatch({ type: 'SET_URL_INIT_FLAG', payload: false });

			dispatch({
				type: FilterActionTypes.APPLY_FILTERS,
				payload: { isNewPageLoad, preservedParams: preservedParams.toString() },
			});
		} else {
			// console.log('FilterContext - URL initialization - no URL filters, applying current filters');
			// Just apply current filters if no URL parameters - don't clear existing state
			dispatch({
				type: FilterActionTypes.APPLY_FILTERS,
				payload: { isNewPageLoad, preservedParams: preservedParams.toString() },
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, filterParamsString]);

	/**
	 * Effect specifically for handling invalid zipcode URL parameters.
	 * Runs when search params change to clean invalid zipcodes.
	 * Only runs when zipcode is the ONLY invalid param (other invalid params are handled by main effect).
	 */
	useEffect(() => {
		// Skip if we just removed invalid params in the main effect
		if (justRemovedInvalidParamsRef.current || location.state?.invalidParamsRemoved) {
			return;
		}

		const params = new URLSearchParams(location.search);
		const zipFromUrl = params.get(URL_PARAM_MAPPING.zipCode.shortCode);

		// Only process if there's an invalid zipcode
		if (zipFromUrl && !/^\d{5}$/.test(zipFromUrl)) {
			const radiusFromUrl = params.get(URL_PARAM_MAPPING.radius.shortCode);

			// Store invalid values for form population
			const invalidZipForForm = zipFromUrl;
			const invalidRadiusForForm = radiusFromUrl;

			// Remove invalid zipcode and radius from URL
			params.delete(URL_PARAM_MAPPING.zipCode.shortCode);
			params.delete(URL_PARAM_MAPPING.radius.shortCode);

			// Update URL without the invalid parameters
			const newSearch = params.toString();

			// Use setTimeout to avoid potential infinite loops
			setTimeout(() => {
				navigate(
					{
						pathname: location.pathname,
						search: newSearch ? `?${newSearch}` : '',
					},
					{ replace: true }
				);
			}, 0);

			// Add invalid zipcode to filters for form population
			const filtersFromUrl = {
				location: {
					zipCode: invalidZipForForm,
					radius: invalidRadiusForForm || '100',
				},
			};

			// Set flag that we're initializing from URL
			dispatch({ type: 'SET_URL_INIT_FLAG', payload: true });

			Object.entries(filtersFromUrl).forEach(([filterType, value]) => {
				dispatch({
					type: FilterActionTypes.SET_FILTER,
					payload: { filterType, value, isNewPageLoad: true },
				});
			});

			// Clear the flag after all filters are set
			dispatch({ type: 'SET_URL_INIT_FLAG', payload: false });

			dispatch({
				type: FilterActionTypes.APPLY_FILTERS,
				payload: { isNewPageLoad: true, preservedParams: '' },
			});
		}
	}, [location.search]);

	/**
	 * Helper function to validate ZIP code and apply filters.
	 * Memoized to prevent infinite loops in useEffect dependencies.
	 *
	 * @param {string} zipCode - ZIP code to validate
	 * @param {string} radius - Search radius in miles
	 */
	const validateZipcodeAndApplyFilters = useCallback(
		async (zipCode, radius) => {
			// Set the filter values first (this won't trigger a search yet)
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: {
					filterType: 'location',
					value: {
						zipCode: zipCode,
						radius: radius || '100',
					},
				},
			});

			// Validate the zipcode via API if format is valid
			if (isValidZipFormat(zipCode)) {
				try {
					// Get the zipcode conversion endpoint from the component state
					const zipBase = zipConversionEndpoint || 'https://clinicaltrialsapi.cancer.gov/api/v2/zip_coords';
					const url = `${zipBase}/${zipCode}`;

					// Call the API directly
					const response = await axios.get(url);

					// Only if we get valid coordinates...
					if (response.data && !response.data.message) {
						// Set the coordinates
						dispatch({
							type: FilterActionTypes.SET_ZIP_COORDINATES,
							payload: response.data,
						});

						// For ZIP validation, apply immediately after coordinates are set
						if (AUTO_APPLY_ENABLED) {
							// Apply filters immediately for location changes
							setTimeout(() => {
								dispatch({ type: FilterActionTypes.APPLY_FILTERS });
							}, 500);
						} else {
							// Legacy behavior: apply immediately
							dispatch({ type: FilterActionTypes.APPLY_FILTERS });
						}
					} else {
						// Don't apply filters with invalid zipcode
						// Invalid ZIP code - silently handle
					}
				} catch (error) {
					// Don't apply filters if validation fails
					// Error validating ZIP - silently handle
				}
			} else {
				// Invalid format - just apply filters normally
				// (The filter won't be used since we need coordinates)
				dispatch({ type: FilterActionTypes.APPLY_FILTERS });
			}
		},
		[zipConversionEndpoint, dispatch]
	);

	/**
	 * Effect to handle ZIP code validation when URL parameters change.
	 * Extracts ZIP and radius from URL and validates if present.
	 */
	useEffect(() => {
		// Skip if we just removed invalid params in the main effect
		if (justRemovedInvalidParamsRef.current || location.state?.invalidParamsRemoved) {
			return;
		}

		// Get ZIP and radius from URL parameters
		const params = new URLSearchParams(location.search);
		const zipParam = params.get(URL_PARAM_MAPPING.zipCode.shortCode);
		const radiusParam = params.get(URL_PARAM_MAPPING.radius.shortCode);

		// If we have a valid ZIP code from URL parameters, handle it specially
		if (zipParam && /^\d{5}$/.test(zipParam)) {
			validateZipcodeAndApplyFilters(zipParam, radiusParam);
		}
	}, [location.pathname, location.search, zipConversionEndpoint, validateZipcodeAndApplyFilters]);

	/**
	 * Effect to handle auto-apply timer when filters change.
	 * Automatically applies filters after a delay when pendingAutoApply is true.
	 */
	useEffect(() => {
		if (state.pendingAutoApply && AUTO_APPLY_ENABLED) {
			// Clear existing timer if it exists
			if (state.autoApplyTimerId) {
				clearTimeout(state.autoApplyTimerId);
			}

			// Get delay based on last changed filter type
			const delay = AUTO_APPLY_DELAYS_BY_TYPE[state.lastChangedFilter] || AUTO_APPLY_DELAY_MS;

			// Set new timer for auto-apply
			const timerId = setTimeout(() => {
				// console.log('FilterContext - Auto-apply timer fired - current filters:', state.filters);
				// console.log('FilterContext - Auto-apply timer fired - lastChangedFilter:', state.lastChangedFilter);

				dispatch({ type: FilterActionTypes.START_AUTO_APPLY });
				dispatch({ type: FilterActionTypes.APPLY_FILTERS });
			}, delay);

			// Store the timer ID
			dispatch({
				type: FilterActionTypes.SCHEDULE_AUTO_APPLY,
				payload: { timerId },
			});

			// Cleanup function to clear timer on unmount
			return () => {
				if (timerId) {
					clearTimeout(timerId);
				}
			};
		}
	}, [state.pendingAutoApply, state.filters, state.lastChangedFilter]);

	/**
	 * Effect to update URL parameters when filters are applied.
	 * Synchronizes the URL with the current filter state.
	 */
	useEffect(() => {
		if (!state.isDirty && state.shouldSearch) {
			// console.log('FilterContext - URL sync effect - isDirty:', state.isDirty, 'shouldSearch:', state.shouldSearch);
			let params = new URLSearchParams(window.location.search);
			let isInitialLoad = state.isInitialLoad;
			const originalPn = params.get('pn');

			// Preserve non-filter URL parameters
			let paramOrder = Array.from(params.keys());
			let filterParams = [URL_PARAM_MAPPING.maintype.shortCode, URL_PARAM_MAPPING.subtype.shortCode, URL_PARAM_MAPPING.stage.shortCode, URL_PARAM_MAPPING.drugIntervention.shortCode, URL_PARAM_MAPPING.age.shortCode, URL_PARAM_MAPPING.zipCode.shortCode, URL_PARAM_MAPPING.radius.shortCode];
			let updatedParams = new Map();

			// Handle non-filter parameters
			for (const key of paramOrder) {
				if (!filterParams.includes(key)) {
					if (key === 'pn' && isInitialLoad) {
						updatedParams.set(key, originalPn);
					} else if (key === 'redirect') {
						// Set the redirect param on the initial loads but ignore otherwise
						if (isInitialLoad) {
							updatedParams.set(key, params.get(key));
						}
						// When filters are applied (not initial load), we don't include redirect=true
					} else {
						updatedParams.set(key, params.get(key));
					}
				}
			}

			// Add filter parameters if they exist
			if (Object.keys(state.appliedFilters).length > 0) {
				// Handle maintype parameter
				if (state.appliedFilters.maintype?.length > 0 && state.appliedFilters.maintype[0]) {
					const maintypeIndex = paramOrder.indexOf(URL_PARAM_MAPPING.maintype.shortCode);
					if (maintypeIndex >= 0) {
						// Preserve parameter order if maintype already exists in URL
						const temp = new Map();
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) < maintypeIndex) {
								temp.set(key, value);
							}
						}
						temp.set(URL_PARAM_MAPPING.maintype.shortCode, state.appliedFilters.maintype[0]);
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) > maintypeIndex) {
								temp.set(key, value);
							}
						}
						updatedParams = temp;
					} else {
						updatedParams.set(URL_PARAM_MAPPING.maintype.shortCode, state.appliedFilters.maintype[0]);
					}
				}

				// Handle subtype parameter
				if (state.appliedFilters.subtype?.length > 0 && state.appliedFilters.subtype[0]) {
					const subtypeIndex = paramOrder.indexOf(URL_PARAM_MAPPING.subtype.shortCode);
					if (subtypeIndex >= 0) {
						// Preserve parameter order if subtype already exists in URL
						const temp = new Map();
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) < subtypeIndex) {
								temp.set(key, value);
							}
						}
						temp.set(URL_PARAM_MAPPING.subtype.shortCode, state.appliedFilters.subtype[0]);
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) > subtypeIndex) {
								temp.set(key, value);
							}
						}
						updatedParams = temp;
					} else {
						updatedParams.set(URL_PARAM_MAPPING.subtype.shortCode, state.appliedFilters.subtype[0]);
					}
				}

				// Handle stage parameter
				if (state.appliedFilters.stage?.length > 0 && state.appliedFilters.stage[0]) {
					const stageIndex = paramOrder.indexOf(URL_PARAM_MAPPING.stage.shortCode);
					if (stageIndex >= 0) {
						// Preserve parameter order if subtype already exists in URL
						const temp = new Map();
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) < stageIndex) {
								temp.set(key, value);
							}
						}
						temp.set(URL_PARAM_MAPPING.stage.shortCode, state.appliedFilters.stage[0]);
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) > stageIndex) {
								temp.set(key, value);
							}
						}
						updatedParams = temp;
					} else {
						updatedParams.set(URL_PARAM_MAPPING.stage.shortCode, state.appliedFilters.stage[0]);
					}
				}

				// Handle age parameter
				if (state.appliedFilters.age?.toString().trim() !== '') {
					const ageIndex = paramOrder.indexOf(URL_PARAM_MAPPING.age.shortCode);
					if (ageIndex >= 0) {
						// Preserve parameter order if age already exists in URL
						const temp = new Map();
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) < ageIndex) {
								temp.set(key, value);
							}
						}
						temp.set(URL_PARAM_MAPPING.age.shortCode, state.appliedFilters.age);
						for (const [key, value] of updatedParams.entries()) {
							if (paramOrder.indexOf(key) > ageIndex) {
								temp.set(key, value);
							}
						}
						updatedParams = temp;
					} else {
						updatedParams.set(URL_PARAM_MAPPING.age.shortCode, state.appliedFilters.age);
					}
				}

				// Handle drugIntervention parameter
				if (state.appliedFilters.drugIntervention?.length > 0) {
					let drugCodes = [];

					// Handle both drug objects and concept code strings
					state.appliedFilters.drugIntervention.forEach((item) => {
						if (typeof item === 'string') {
							// Direct concept code
							drugCodes.push(item);
						} else if (item && item.codes && item.codes[0]) {
							// Drug object with codes array
							drugCodes.push(item.codes[0]);
						}
					});

					if (drugCodes.length > 0) {
						updatedParams.set(URL_PARAM_MAPPING.drugIntervention.shortCode, drugCodes.join(','));
					}
				}

				// Handle location parameters
				if (state.appliedFilters.location?.zipCode) {
					updatedParams.set(URL_PARAM_MAPPING.zipCode.shortCode, state.appliedFilters.location.zipCode);
					if (state.appliedFilters.location.radius) {
						updatedParams.set(URL_PARAM_MAPPING.radius.shortCode, state.appliedFilters.location.radius.toString());
					}
				}
			}

			// Handle pagination parameter
			const pnValue = isInitialLoad || !state.isDirty ? originalPn || '1' : '1';
			const pnIndex = paramOrder.indexOf('pn');
			if (pnIndex >= 0) {
				// Preserve parameter order if 'pn' already exists
				const temp = new Map();
				for (const [key, value] of updatedParams.entries()) {
					if (paramOrder.indexOf(key) < pnIndex) {
						temp.set(key, value);
					}
				}
				temp.set('pn', pnValue);
				for (const [key, value] of updatedParams.entries()) {
					if (paramOrder.indexOf(key) > pnIndex) {
						temp.set(key, value);
					}
				}
				updatedParams = temp;
			} else {
				updatedParams.set('pn', pnValue);
			}
			// Construct the final query string
			const queryString = Array.from(updatedParams.entries())
				.filter(([, value]) => {
					// Check if value is null or undefined before calling toString()
					return value !== null && value !== undefined && value.toString().trim() !== '';
				})
				.map(([key, value]) => `${key}=${value}`)
				.join('&');

			// Update the URL - create history entry for user-initiated filter changes, replace for initial loads
			const shouldReplace = state.isInitialLoad;
			// console.log('FilterContext - URL update - shouldReplace:', shouldReplace, 'isInitialLoad:', state.isInitialLoad);
			const currentSearch = window.location.search;
			const newSearch = queryString ? `?${queryString}` : '';

			// console.log('FilterContext - URL update - current URL:', window.location.href);
			// console.log('FilterContext - URL update - current search:', currentSearch);
			// console.log('FilterContext - URL update - new search params:', newSearch);

			// Only navigate if the URL would actually change
			if (currentSearch !== newSearch) {
				// console.log('FilterContext - URL update - URL changed, navigating with replace:', shouldReplace);
				navigate(
					{
						pathname: location.pathname,
						search: newSearch,
					},
					{
						replace: shouldReplace,
						// Signal to ScrollRestoration that this is a filter update
						// so it doesn't scroll to top when users interact with filters
						// Also signal if there's an error so it can scroll to show the error message
						state: {
							filterUpdate: true,
							scrollToError: state.isInvalidQuery,
						},
					}
				);
			} else {
				// console.log('FilterContext - URL update - URL unchanged, skipping navigation');
			}

			// Reset isInitialLoad flag after URL sync completes
			// This ensures analytics fire correctly for subsequent user interactions
			if (state.isInitialLoad) {
				const resetTimer = setTimeout(() => {
					dispatch({ type: FilterActionTypes.RESET_INITIAL_LOAD });
				}, 100); // Small delay to ensure navigation completes
				return () => clearTimeout(resetTimer);
			}
		}
	}, [state.appliedFilters, state.isDirty, state.shouldSearch, state.isInitialLoad, location.pathname, navigate]);

	/**
	 * Transforms the applied filter state into the format expected by the API.
	 *
	 * @param {object} filters - The applied filter state object.
	 * @returns {object} Filters formatted for the API request.
	 */
	const transformFiltersToApi = (filters) => {
		// console.log('FilterContext - transformFiltersToApi - input filters:', filters);
		let apiFilters = {};

		// Transform age filter
		if (filters.age?.toString().trim() !== '') {
			apiFilters['eligibility.structured.min_age_in_years_lte'] = filters.age;
			apiFilters['eligibility.structured.max_age_in_years_gte'] = filters.age;
		}

		// Transform maintype filter
		if (filters.maintype && Array.isArray(filters.maintype) && filters.maintype.length > 0) {
			// console.log('FilterContext - transformFiltersToApi - maintype found:', filters.maintype);
			apiFilters['maintype'] = filters.maintype;
		}

		// Transform subtype filter
		if (filters.subtype && Array.isArray(filters.subtype) && filters.subtype.length > 0) {
			apiFilters['subtype'] = filters.subtype;
		}

		// Transform stage filter
		if (filters.stage && Array.isArray(filters.stage) && filters.stage.length > 0) {
			apiFilters['stage'] = filters.stage;
		}

		// Transform drugIntervention filter
		if (filters.drugIntervention && Array.isArray(filters.drugIntervention) && filters.drugIntervention.length > 0) {
			let drugCodes = [];

			// Handle both drug objects and concept code strings
			filters.drugIntervention.forEach((item) => {
				if (typeof item === 'string') {
					// Direct concept code from URL
					drugCodes.push(item);
				} else if (item && item.codes && item.codes[0]) {
					// Drug object with codes array
					drugCodes.push(item.codes[0]);
				}
			});

			if (drugCodes.length > 0) {
				apiFilters['arms.interventions.nci_thesaurus_concept_id'] = drugCodes;
			}
		}

		// Transform location filter using utility function
		const locationFilters = getLocationFilters(filters.location, state.appliedZipCoords);

		const finalApiFilters = {
			...apiFilters,
			...locationFilters,
		};

		// console.log('FilterContext - transformFiltersToApi - final API filters:', finalApiFilters);
		return finalApiFilters;
	};

	/**
	 * Gets the current filters (base filters + applied filters) formatted for the API.
	 *
	 * @returns {object} Current filters ready for API request.
	 */
	const getCurrentFilters = () => ({
		...state.baseFilters,
		...transformFiltersToApi(state.appliedFilters),
	});

	// Value provided by the context
	const value = {
		state,
		dispatch,
		getCurrentFilters,
		isApplyingFilters,
		setIsApplyingFilters,
		enabledFilters: filterConfig.enabledFilters,
		pageType,
		zipCoords: state.zipCoords,
		appliedZipCoords: state.appliedZipCoords,
		listingInfo: {
			diseaseName: pageType === 'Disease' ? baseFilters.diseaseName : null,
			interventionName: pageType === 'Intervention' ? baseFilters.interventionName : null,
			trialType: baseFilters.trialType,
			pageTitle: baseFilters.pageTitle,
		},
	};

	return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

// PropTypes for the FilterProvider component
FilterProvider.propTypes = {
	children: PropTypes.node.isRequired,
	baseFilters: PropTypes.object,
	pageType: PropTypes.string,
};

/**
 * Custom hook for accessing the FilterContext.
 * Provides convenient access to state and action dispatchers.
 *
 * @returns {object} Filter context values (state, dispatch, actions, etc.)
 */
export function useFilters() {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error('useFilters must be used within a FilterProvider');
	}

	const { state, dispatch, getCurrentFilters, isApplyingFilters, setIsApplyingFilters, enabledFilters, zipCoords, appliedZipCoords, pageType } = context;

	/**
	 * Dispatches an action to set a specific filter value.
	 * @param {string} filterType - The type of filter to set.
	 * @param {*} value - The value to set for the filter.
	 */
	const setFilter = (filterType, value) =>
		dispatch({
			type: FilterActionTypes.SET_FILTER,
			payload: { filterType, value },
		});

	/**
	 * Dispatches an action to apply the current filter state.
	 * Includes a small delay for visual feedback.
	 */
	const applyFilters = async () => {
		setIsApplyingFilters(true);
		dispatch({ type: FilterActionTypes.APPLY_FILTERS });
		// Artificial delay for visual feedback (optional)
		await new Promise((resolve) => setTimeout(resolve, 300));
		setIsApplyingFilters(false);
	};

	/**
	 * Dispatches an action to clear all filters.
	 */
	const clearFilters = () => dispatch({ type: FilterActionTypes.CLEAR_FILTERS });

	/**
	 * Dispatches an action to remove a specific filter value.
	 * @param {string} filterType - The type of filter to remove from.
	 * @param {*} value - The specific value to remove.
	 */
	const removeFilter = (filterType, value) =>
		dispatch({
			type: FilterActionTypes.REMOVE_FILTER,
			payload: { filterType, value },
		});

	// Return context values and action functions
	return {
		state,
		dispatch,
		getCurrentFilters,
		setFilter,
		applyFilters,
		clearFilters,
		removeFilter,
		isApplyingFilters,
		enabledFilters,
		pageType,
		zipCoords,
		appliedZipCoords,
	};
}
